import { Injectable } from '@angular/core';
import { Observable, map, catchError, of, BehaviorSubject } from 'rxjs';
import { ApiClientService } from '@core/services/api-client.service';

export interface StatusOption {
  id: string;
  Name: string;
  DisplayName: string;
}

export interface StatusDropdownOption {
  id: string;
  Name: string;
  DisplayName?: string;
}

export interface CustomerStatusUpdateRequest {
  status: string;
  CustomerId?: string | number;
  rejectionReason?: string;
  deEnrollCode?: string;
  transException?: string;
  terminationCode?: string;
}

export interface CustomerStatusUpdateResponse {
  Status: boolean;
  message: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class CustomerStatusService {
  private readonly statusOptionsSubject = new BehaviorSubject<StatusOption[]>([]);
  public readonly statusOptions$ = this.statusOptionsSubject.asObservable();

  private readonly rejectionReasonsSubject = new BehaviorSubject<StatusDropdownOption[]>([]);
  public readonly rejectionReasons$ = this.rejectionReasonsSubject.asObservable();

  private readonly deEnrollCodesSubject = new BehaviorSubject<StatusDropdownOption[]>([]);
  public readonly deEnrollCodes$ = this.deEnrollCodesSubject.asObservable();

  private readonly transExceptionsSubject = new BehaviorSubject<StatusDropdownOption[]>([]);
  public readonly transExceptions$ = this.transExceptionsSubject.asObservable();

  private readonly terminationCodesSubject = new BehaviorSubject<StatusDropdownOption[]>([]);
  public readonly terminationCodes$ = this.terminationCodesSubject.asObservable();

  constructor(private apiClient: ApiClientService) {}

  /**
   * Get available status options for customer
   */
  getStatusOptions(): Observable<{ Status: boolean; data: StatusOption[]; message?: string }> {
    return this.apiClient.get<{ Status: boolean; data: StatusOption[] }>('api/Customer/GetCustomerStatus', {
      showLoading: false
    }).pipe(
      map(response => {
        if (response.Status && response.data && Array.isArray(response.data)) {
          this.statusOptionsSubject.next(response.data);
        }
        return {
          Status: response.Status,
          data: Array.isArray(response.data) ? response.data : [],
          message: response.message
        };
      }),
      catchError(error => {
        console.error('Error getting status options:', error);
        return of({
          Status: false,
          data: [],
          message: error.message || 'Failed to load status options'
        });
      })
    );
  }

  /**
   * Get rejection reason options
   */
  getRejectionReasons(): Observable<{ Status: boolean; data: StatusDropdownOption[]; message?: string }> {
    return this.apiClient.get<{ Status: boolean; data: StatusDropdownOption[] }>('api/Customer/GetRejections', {
      showLoading: false
    }).pipe(
      map(response => {
        if (response.Status && response.data && Array.isArray(response.data)) {
          this.rejectionReasonsSubject.next(response.data);
        }
        return {
          Status: response.Status,
          data: Array.isArray(response.data) ? response.data : [],
          message: response.message
        };
      }),
      catchError(error => {
        console.error('Error getting rejection reasons:', error);
        return of({
          Status: false,
          data: [],
          message: error.message || 'Failed to load rejection reasons'
        });
      })
    );
  }

  /**
   * Get de-enrollment code options
   */
  getDeEnrollCodes(): Observable<{ Status: boolean; data: StatusDropdownOption[]; message?: string }> {
    return this.apiClient.get<{ Status: boolean; data: StatusDropdownOption[] }>('api/Customer/GetNladDeEnrollCodes', {
      showLoading: false
    }).pipe(
      map(response => {
        if (response.Status && response.data && Array.isArray(response.data)) {
          this.deEnrollCodesSubject.next(response.data);
        }
        return {
          Status: response.Status,
          data: Array.isArray(response.data) ? response.data : [],
          message: response.message
        };
      }),
      catchError(error => {
        console.error('Error getting de-enrollment codes:', error);
        return of({
          Status: false,
          data: [],
          message: error.message || 'Failed to load de-enrollment codes'
        });
      })
    );
  }

  /**
   * Get transaction exception options
   */
  getTransExceptions(): Observable<{ Status: boolean; data: StatusDropdownOption[]; message?: string }> {
    return this.apiClient.get<{ Status: boolean; data: StatusDropdownOption[] }>('api/Customer/getExceptionCodes', {
      showLoading: false
    }).pipe(
      map(response => {
        if (response.Status && response.data && Array.isArray(response.data)) {
          this.transExceptionsSubject.next(response.data);
        }
        return {
          Status: response.Status,
          data: Array.isArray(response.data) ? response.data : [],
          message: response.message
        };
      }),
      catchError(error => {
        console.error('Error getting transaction exceptions:', error);
        return of({
          Status: false,
          data: [],
          message: error.message || 'Failed to load transaction exceptions'
        });
      })
    );
  }

  /**
   * Get termination code options for UCL customers
   */
  getTerminationCodes(): Observable<{ Status: boolean; data: StatusDropdownOption[]; message?: string }> {
    return this.apiClient.get<{ Status: boolean; data: { data: StatusDropdownOption[] } }>('api/Customer/getTerminationCodes', {
      showLoading: false
    }).pipe(
      map(response => {
        const codes = response.data?.data || [];
        if (response.Status && Array.isArray(codes) && codes.length > 0) {
          this.terminationCodesSubject.next(codes);
        }
        return {
          Status: response.Status,
          data: Array.isArray(codes) ? codes : [],
          message: response.message
        };
      }),
      catchError(error => {
        console.error('Error getting termination codes:', error);
        return of({
          Status: false,
          data: [],
          message: error.message || 'Failed to load termination codes'
        });
      })
    );
  }

  /**
   * Update customer status
   */
  updateCustomerStatus(
    data: CustomerStatusUpdateRequest, 
    type: 'electronic' | 'non-electronic' = 'electronic'
  ): Observable<CustomerStatusUpdateResponse> {
    const url = type === 'electronic' 
      ? 'api/Customer/CustomerStatus'
      : 'api/Customer/ChangeStatusSystem';

    // Structure payload according to old codebase format
    const payload = {
      companyId: 'UNITYCRM0021',
      CustomerId: data.CustomerId,
      status: data.status,
      ...((data.rejectionReason && { rejectionReason: data.rejectionReason })),
      ...((data.deEnrollCode && { deEnrollCode: data.deEnrollCode })),
      ...((data.transException && { transException: data.transException })),
      ...((data.terminationCode && { terminationCode: data.terminationCode }))
    };

    console.log('API Payload being sent:', payload);

    return this.apiClient.post<CustomerStatusUpdateResponse>(url, payload, {
      showLoading: true
    }).pipe(
      map(response => ({
        Status: response.Status,
        message: response.message || 'Status updated successfully',
        data: response.data
      })),
      catchError(error => {
        console.error('Error updating customer status:', error);
        return of({
          Status: false,
          message: error?.error?.message || error.message || 'Failed to update customer status'
        });
      })
    );
  }

  /**
   * Clear all cached dropdown options
   */
  clearCache(): void {
    this.rejectionReasonsSubject.next([]);
    this.deEnrollCodesSubject.next([]);
    this.transExceptionsSubject.next([]);
    this.terminationCodesSubject.next([]);
  }

  /**
   * Get current status options
   */
  getCurrentStatusOptions(): StatusOption[] {
    return this.statusOptionsSubject.value;
  }

  /**
   * Get current rejection reasons
   */
  getCurrentRejectionReasons(): StatusDropdownOption[] {
    return this.rejectionReasonsSubject.value;
  }

  /**
   * Get current de-enrollment codes
   */
  getCurrentDeEnrollCodes(): StatusDropdownOption[] {
    return this.deEnrollCodesSubject.value;
  }

  /**
   * Get current transaction exceptions
   */
  getCurrentTransExceptions(): StatusDropdownOption[] {
    return this.transExceptionsSubject.value;
  }

  /**
   * Get current termination codes
   */
  getCurrentTerminationCodes(): StatusDropdownOption[] {
    return this.terminationCodesSubject.value;
  }
}